import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { Button } from 'antd';
import styled from 'styled-components';

import { getCommunitiesPreferences } from 'apis/CommunityAPI';
import Notification from 'components/Notification/Notification';
import { CommunityPreferenceType } from 'types/communities';
import { useDebounce } from 'utils/hooks/useDebounce';

import CommunitiesList from './CommunitiesList';
import { SETTINGS_PAGE_INFO } from './constants';
import { useRemoveCommunitiesSelection } from './customHooks';
import { SettingsContext } from './SettingsContext';
import SettingsHeader from './SettingsHeader';

const Container = styled.div`
  padding-bottom: 30px;
  width: 100%;
  background-color: white;
`;

const StyledEditButton = styled(Button)`
  background-color: var(--button-blue);
  border-color: var(--button-blue);
  padding: 10px 30px 10px 30px;
  height: 40px;
  font-family: var(--font-regular);
  border-radius: 4px;

  &:hover {
    background-color: var(--button-blue-hover) !important;
  }
`;

type Props = {
  filterOptions?: { value: number | null; label: string }[];
  filterField?: string;
  selectPopupWidth?: number;
  statusFilterWidth?: number;
  hideFilters?: boolean;
  extraHeaders?: string[];
  renderExtraColumns?: (item: any) => React.ReactNode[];
  settingsPageType: string;
  notificationType?: number;
  setRefetchCommunities?: (func: () => void) => void;
  getItemStatusComponent: (item: any) => React.ReactNode;
  showCommunitiesEditModal: (items: any[]) => void;
  setClearSelection?: (func: () => void) => void;
};

const SettingsPage = ({
  filterOptions,
  filterField,
  selectPopupWidth,
  statusFilterWidth,
  settingsPageType,
  hideFilters = false,
  extraHeaders = [],
  renderExtraColumns,
  notificationType,
  setRefetchCommunities,
  getItemStatusComponent,
  showCommunitiesEditModal,
  setClearSelection,
}: Props) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [communitiesTotalCount, setCommunitiesTotalCount] = useState(0);
  const [status, setStatus] = useState<number | null>(null);
  const [selectedCommunitiesIds, setSelectedCommunitiesIds] = useState<number[]>([]);
  const [selectedCommunities, setSelectedCommunities] = useState<CommunityPreferenceType[]>([]);
  const [communities, setCommunities] = useState<CommunityPreferenceType[]>([]);
  const [isMultipleSelection, setMultipleSelection] = useState(false);
  const [companiesFilter, setCompaniesFilter] = useState<number[]>([]);

  const debouncedSearch = useDebounce(searchTerm);

  const maxSelectedCount = 300;

  useRemoveCommunitiesSelection(isMultipleSelection, selectedCommunitiesIds.length, setSelectedCommunitiesIds);

  const { isFetching: isFetchingCommunities, refetch } = useQuery({
    queryKey: ['settings', 'communities', debouncedSearch, page, pageSize, companiesFilter, status, filterField],
    queryFn: () =>
      getCommunitiesPreferences({
        page,
        search: debouncedSearch,
        page_size: pageSize,
        companies: companiesFilter,
        status,
        status_field: filterField,
        notification_type: notificationType,
      }),
    onSuccess: (response) => {
      setCommunities(response.results);
      setCommunitiesTotalCount(response.count);
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (setRefetchCommunities && typeof refetch === 'function') {
      setRefetchCommunities(() => refetch);
    }
  }, [setRefetchCommunities, refetch]);

  useEffect(() => {
    if (setClearSelection) {
      setClearSelection(() => () => {
        setSelectedCommunitiesIds([]);
        setSelectedCommunities([]);
        setMultipleSelection(false);
      });
    }
  }, [setClearSelection]);

  const onShowCommunitiesEditModal = useCallback(() => {
    showCommunitiesEditModal(selectedCommunities);
  }, [communities, selectedCommunitiesIds]);

  const toggleOneCommunity = useCallback(
    (community: CommunityPreferenceType) => {
      const isSelected = selectedCommunitiesIds.includes(community.id);
      if (isSelected) {
        setSelectedCommunitiesIds((prev) => prev.filter((id) => id !== community.id));
        setSelectedCommunities((prev) => prev.filter((c) => c.id !== community.id));
      } else {
        setSelectedCommunitiesIds((prev) => [...prev, community.id]);
        setSelectedCommunities((prev) => [...prev, community]);
      }
    },
    [selectedCommunitiesIds, setSelectedCommunitiesIds]
  );

  const toggleAllCommunities = useCallback(() => {
    const currentPageIds = communities.map((c) => c.id);

    const areAllSelectedOnPage = currentPageIds.every((id) => selectedCommunitiesIds.includes(id));

    if (areAllSelectedOnPage) {
      // Unselect only current page
      setSelectedCommunitiesIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
      setSelectedCommunities((prev) => prev.filter((c) => !currentPageIds.includes(c.id)));
    } else {
      // Add current page to selections, keep previous pages
      setSelectedCommunitiesIds((prev) => [...new Set([...prev, ...currentPageIds])]);
      setSelectedCommunities((prev) => {
        const merged = [...prev];
        communities.forEach((c) => {
          if (!merged.find((x) => x.id === c.id)) {
            merged.push(c);
          }
        });
        return merged;
      });
    }
  }, [communities, selectedCommunitiesIds]);

  const allCommunitiesSelected =
    communities.length > 0 && communities.every((c) => selectedCommunitiesIds.includes(c.id));

  const selectAllItems = useCallback(async () => {
    const response = await getCommunitiesPreferences({
      page: 1,
      page_size: communitiesTotalCount > maxSelectedCount ? maxSelectedCount : communitiesTotalCount,
      companies: companiesFilter,
      status,
      status_field: filterField,
      notification_type: notificationType,
    });

    const allIds = response.results.map((c: CommunityPreferenceType) => c.id);

    setSelectedCommunitiesIds(allIds);
    setSelectedCommunities(response.results);
  }, [communitiesTotalCount, companiesFilter, status, notificationType]);

  return (
    <SettingsContext.Provider
      value={{
        filterOptions: filterOptions
          ? [
              {
                value: null,
                label: 'Any status',
              },
              ...Object.values(filterOptions),
            ]
          : [],
        isMultipleSelection,
        page,
        pageSize,
        searchTerm,
        status,
        communities,
        communitiesTotalCount,
        selectedCommunitiesIds,
        allCommunitiesSelected,
        headerContent: SETTINGS_PAGE_INFO[settingsPageType],
        loadingCommunities: isFetchingCommunities,
        companiesFilter,
        setPageSize,
        getItemStatusComponent,
        toggleAllCommunities,
        toggleOneCommunity,
        setCompaniesFilter,
      }}
    >
      <Container>
        <Notification
          allItemsCount={communitiesTotalCount}
          maxCount={maxSelectedCount}
          totalCount={communities.length}
          selectedCount={selectedCommunitiesIds.length}
          singularLabel={'community'}
          multipleLabel={'communities'}
          onSelectAllItems={selectAllItems}
          onClose={() => {
            setSelectedCommunitiesIds([]);
            setMultipleSelection(false);
          }}
          actions={
            <StyledEditButton type="primary" onClick={onShowCommunitiesEditModal}>
              Edit Multiple
            </StyledEditButton>
          }
        />
        <SettingsHeader />
        <CommunitiesList
          hideFilters={hideFilters}
          selectPopupWidth={selectPopupWidth}
          statusFilterWidth={statusFilterWidth}
          extraHeaders={extraHeaders}
          renderExtraColumns={renderExtraColumns}
          toggleMultipleSelection={() => setMultipleSelection(!isMultipleSelection)}
          setPage={setPage}
          setSearchTerm={setSearchTerm}
          setStatus={setStatus}
          modifyCommunity={(item: any) => {
            if (!isMultipleSelection) {
              showCommunitiesEditModal([item]);
            }
          }}
        />
      </Container>
    </SettingsContext.Provider>
  );
};

export default SettingsPage;
